import React from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3 } from 'lucide-react';
import { CLIMATE_AREAS, TRAIN_PROGRESS, WR_COMPLIANCE, MAP_ROLES, SUCC_GAUGES, SEMANTIC, climateColor } from '@/data/landing2Data';
import { SuiteGlyph } from './SuiteVisuals';

function CardShell({ sid, title, tag, children, testid }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <SuiteGlyph sid={sid} className="h-4 w-4" />
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">{title}</p>
        </div>
        <span className="text-[9px] uppercase tracking-wider text-slate-400 border border-slate-200 rounded-full px-2 py-0.5">{tag}</span>
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

export default function AnalyticsSection() {
  const { t } = useTranslation();
  const L = (k) => t(`pages.landing2.analytics.${k}`);
  return (
    <section id="resultados" className="max-w-7xl mx-auto px-6 py-20 lg:py-24" data-testid="analytics-section">
      <div className="max-w-3xl mb-12">
        <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-100 px-4 py-1.5 text-xs font-semibold text-emerald-700">
          <BarChart3 className="h-3.5 w-3.5" /> {L('eyebrow')}
        </span>
        <h2 className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight">{L('heading')}</h2>
        <p className="mt-4 text-slate-600 md:text-lg">{L('sub')}</p>
        <p className="mt-3 text-xs text-slate-400 italic" data-testid="analytics-disclaimer">{L('disclaimer')}</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Climara — climate by area, semantic colors */}
        <CardShell sid="climara" title={L('climaraTitle')} tag={L('illustrative')} testid="analytics-climara">
          <div className="space-y-3">
            {CLIMATE_AREAS.map((a) => (
              <div key={a.key}>
                <div className="flex justify-between text-sm mb-1"><span className="font-medium">{L(a.key)}</span><span className="tabular-nums font-bold" style={{ color: climateColor(a.v) }}>{a.v}</span></div>
                <div className="h-2 rounded-full bg-slate-100"><div className="h-full rounded-full" style={{ width: `${a.v}%`, background: climateColor(a.v) }} /></div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-3 text-[11px] text-slate-500">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: SEMANTIC.green }} />{L('legendHealthy')}</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: SEMANTIC.amber }} />{L('legendWatch')}</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: SEMANTIC.red }} />{L('legendRisk')}</span>
          </div>
        </CardShell>

        {/* TalentTrain — progress rings */}
        <CardShell sid="talenttrain" title={L('trainTitle')} tag={L('illustrative')} testid="analytics-train">
          <div className="grid grid-cols-3 gap-3 h-full items-center">
            {TRAIN_PROGRESS.map((p) => (
              <div key={p.key} className="text-center">
                <div className="relative h-20 w-20 mx-auto rounded-full grid place-items-center" style={{ background: `conic-gradient(${SEMANTIC.amber} 0 ${p.v}%, #F1F5F9 ${p.v}% 100%)` }}>
                  <div className="h-[3.7rem] w-[3.7rem] rounded-full bg-white grid place-items-center text-base font-extrabold">{p.v}%</div>
                </div>
                <div className="text-xs font-semibold text-slate-600 mt-2">{L(p.key)}</div>
              </div>
            ))}
          </div>
        </CardShell>

        {/* Workforce Records — compliance donut + counters */}
        <CardShell sid="workforcerecords" title={L('wrTitle')} tag={L('illustrative')} testid="analytics-wr">
          <div className="flex items-center gap-6">
            <div className="relative h-28 w-28 rounded-full grid place-items-center shrink-0" style={{ background: `conic-gradient(${SEMANTIC.green} 0 ${WR_COMPLIANCE.pct}%, #F1F5F9 ${WR_COMPLIANCE.pct}% 100%)` }}>
              <div className="h-[5.4rem] w-[5.4rem] rounded-full bg-white grid place-items-center text-2xl font-extrabold" style={{ color: SEMANTIC.green }}>{WR_COMPLIANCE.pct}%</div>
            </div>
            <div className="space-y-2.5 text-sm">
              <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: SEMANTIC.green }} /><span className="font-bold tabular-nums">{WR_COMPLIANCE.complete}</span> {L('wrComplete')}</div>
              <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: SEMANTIC.amber }} /><span className="font-bold tabular-nums">{WR_COMPLIANCE.due}</span> {L('wrDue')}</div>
              <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: SEMANTIC.red }} /><span className="font-bold tabular-nums">{WR_COMPLIANCE.expired}</span> {L('wrExpired')}</div>
            </div>
          </div>
        </CardShell>

        {/* TalentMap — compatibility by role */}
        <CardShell sid="talentmap" title={L('mapTitle')} tag={L('illustrative')} testid="analytics-map">
          <div className="space-y-4">
            {MAP_ROLES.map((r) => (
              <div key={r.key}>
                <div className="flex justify-between text-sm mb-1"><span className="font-medium">{L(r.key)}</span><span className="tabular-nums font-bold" style={{ color: SEMANTIC.blue }}>{r.v}%</span></div>
                <div className="h-2.5 rounded-full bg-slate-100"><div className="h-full rounded-full" style={{ width: `${r.v}%`, background: `linear-gradient(90deg,${SEMANTIC.blue},${SEMANTIC.turquoise})` }} /></div>
              </div>
            ))}
          </div>
        </CardShell>

        {/* Succession — readiness by quadrant (four independent gauges, no overall score) */}
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:col-span-2 lg:col-span-2 flex flex-col" data-testid="analytics-succession">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2.5">
              <SuiteGlyph sid="succession" className="h-4 w-4" />
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">{L('succTitle')}</p>
            </div>
            <span className="text-[9px] uppercase tracking-wider text-slate-400 border border-slate-200 rounded-full px-2 py-0.5">{L('illustrative')}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1 items-center">
            {SUCC_GAUGES.map((g) => (
              <div key={g.key} className="text-center">
                <div className="relative h-20 w-20 mx-auto rounded-full grid place-items-center" style={{ background: `conic-gradient(#0E7490 0 ${g.v}%, #F1F5F9 ${g.v}% 100%)` }}>
                  <div className="h-[3.7rem] w-[3.7rem] rounded-full bg-white grid place-items-center text-base font-extrabold text-[#0E7490]">{g.v}%</div>
                </div>
                <div className="text-xs font-semibold text-slate-600 mt-2">{L(g.key)}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-500">{L('succNote')}</p>
        </div>
      </div>
    </section>
  );
}
